import {
  Backdrop,
  CloseIcon,
  HamburgerMenuIcon,
  MenuSection,
  NavDrawerAccountDetails,
  NavDrawerContainer,
  NavDrawerContent,
  NavDrawerLink,
  NavDrawerLinkSection,
  NavDrawerSeeProfileLink,
  NavDrawerStyledButton,
  ToggleButton,
  FileClaimLinkButton,
  HeaderLinkContainer,
} from './nav-drawer.styles';
import React, { FC, useEffect, useState } from 'react';
import { HeaderAccountIcon } from 'common/components/layout/header/header.styles';
import { ButtonColor, Loader, useAccountName } from '@sapiens/insured-component-library';
import { FlexBox } from 'common/utils/styles/flex-box';
import { HEADER_NAVIGATION_LINKS } from 'common/utils/constants/header.constants';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';
import { handleLogout } from 'modules/auth/utils/helpers/auth.helpers';
import { useTheme } from 'styled-components';
import { THEME_DEFAULTS } from 'common/utils/constants/theme.constants';
import { useCustomerInformation } from 'modules/user-profile/hooks/use-user-details';
import { useUserStore } from 'common/stores/user-store';
import Error from 'common/components/error/errors';
import { Typography } from 'common/components/typography/typography';
import { FontType } from 'common/utils/constants/typography.constants';
import Image from 'next/image';
import { LeaveConfirmation } from '../leave-confirmation/leave-confirmation';
import { NavRouteParams } from '../leave-confirmation/leave-confirmation.model';
import { FileClaimPopup } from '../file-claim-popup/file-claim-popup';

export interface NavDrawerProps {
  unSavedChangesExistInJourney: number;
  unSavedChangesExistInFnol: number;
  onBoardedUser: boolean;
}

const NavDrawer: React.FC<NavDrawerProps> = ({
  unSavedChangesExistInJourney,
  unSavedChangesExistInFnol,
  onBoardedUser,
}) => {
  const [navDrawerOpen, setNavDrawerOpen] = useState(false);
  const { t } = useTranslation();
  const router = useRouter();
  const theme = useTheme();
  const [showQuotePopup, setShowQuotePopup] = useState(false); // Separate state for QUOTE popup
  const [showClaimPopup, setShowClaimPopup] = useState(false);
  const [fileClaimPopup, setFileClaimPopup] = useState(false);
  const [nxtRoute, setNxtRoute] = useState<NavRouteParams>({});

  // Handle all anchor tag links(including on profile link click)
  const checkForUnsavedChanges = (event: any, path: string) => {
    let hasUnsavedChanges = false;
    if (unSavedChangesExistInJourney === 1) {
      event.preventDefault();
      setShowQuotePopup(true);
      hasUnsavedChanges = true;
    }
    if (unSavedChangesExistInFnol === 1) {
      event.preventDefault();
      setShowClaimPopup(true);
      hasUnsavedChanges = true;
    }
    if (hasUnsavedChanges) {
      setNxtRoute({ path: path });
      return false;
    }

    return true;
  };

  /**
   * effect to add event listener on resize
   * and close the nav drawer when resized to a tab or higher resolution
   */
  useEffect(() => {
    const handleBodyScroll = () => {
      if (window.innerWidth > THEME_DEFAULTS.BREAKPOINTS.sm) {
        setNavDrawerOpen(false);
      }
    };

    window.addEventListener('resize', handleBodyScroll);

    return () => {
      window.removeEventListener('resize', handleBodyScroll);
    };
  }, []);

  /**
   * effect to track drawer open state
   * and lock the scroll when open
   */
  // Handle overflow style based on the state of nav drawer and popup
  useEffect(() => {
    if (navDrawerOpen || showQuotePopup || showClaimPopup || fileClaimPopup) {
      document.body.style.overflow = 'hidden';
      document.body.classList.add('ant-scrolling-effect');
    } else {
      document.body.style.overflow = 'auto';
      document.body.classList.remove('ant-scrolling-effect');
    }
  }, [navDrawerOpen, showQuotePopup, showClaimPopup, fileClaimPopup]);

  const toggleDrawer = () => {
    setNavDrawerOpen((prev) => !prev);
  };

  //Handle Profile button, report claim and logout button clicks
  const onButtonClick = (pathname: string) => {
    if (pathname === 'logout') {
      if (unSavedChangesExistInJourney === 1) {
        setShowQuotePopup(true);
      }
      if (unSavedChangesExistInFnol === 1) {
        setShowClaimPopup(true);
      }

      if (unSavedChangesExistInJourney === 1 || unSavedChangesExistInFnol === 1) {
        setNxtRoute({ logout: true });
        return false;
      }

      handleLogout();
    } else if (pathname === '/profile') {
      if (unSavedChangesExistInJourney === 1) {
        setShowQuotePopup(true);
      }
      if (unSavedChangesExistInFnol === 1) {
        setShowClaimPopup(true);
      }

      if (unSavedChangesExistInJourney === 1 || unSavedChangesExistInFnol === 1) {
        setNxtRoute({ path: pathname });
        return false;
      }

      router.push(pathname);
    } else if (pathname === '/policies') {
      if (unSavedChangesExistInJourney === 1) {
        setShowQuotePopup(true);
      }
      if (unSavedChangesExistInFnol === 1) {
        setShowClaimPopup(true);
      }

      if (unSavedChangesExistInJourney === 1 || unSavedChangesExistInFnol === 1) {
        setNxtRoute({ path: pathname, helpParam: true });
        return false;
      }

      router.push({ pathname: pathname, query: { help: true } });
    }
  };

  const FILTERED_HEADER_LINKS = onBoardedUser
    ? HEADER_NAVIGATION_LINKS.filter(({ label }) => label === 'quotes')
    : HEADER_NAVIGATION_LINKS;

  return (
    <>
      <MenuSection>
        <ToggleButton startIcon={<HamburgerMenuIcon />} onClick={toggleDrawer} />
      </MenuSection>

      <NavDrawerContainer show={navDrawerOpen}>
        <ToggleButton startIcon={<CloseIcon />} onClick={toggleDrawer} />

        <NavDrawerContent onClick={toggleDrawer}>
          <NavDrawerAccount onViewProfileClick={checkForUnsavedChanges} onProfileLogoutClick={onButtonClick} />
          <NavDrawerLinkSection>
            <FlexBox flexType="container" direction="column">
              {FILTERED_HEADER_LINKS.map(({ label, path, matcher }) => (
                <NavDrawerLink
                  key={`${label}-${path}`}
                  href={path}
                  isActive={Boolean(router?.pathname.match(matcher))}
                  onClick={(e) => checkForUnsavedChanges(e, path)}
                >
                  {t(`navigationLinks.${label}`)}
                </NavDrawerLink>
              ))}
              <br />
              {!onBoardedUser && (
                <HeaderLinkContainer onClick={() => setFileClaimPopup(true)}>
                  <Image src={'/icons/header-file-claim.svg'} alt="start-date" width="24" height="32" />
                  <FileClaimLinkButton>
                    <Typography fontType={FontType.COPY_SMALL_BOLD} color={theme.palette.neutral.black}>
                      {t('header.fileAClaim')}
                    </Typography>
                  </FileClaimLinkButton>
                </HeaderLinkContainer>
              )}
            </FlexBox>
          </NavDrawerLinkSection>

          <NavDrawerStyledButton color={ButtonColor.primary} onClick={() => onButtonClick('logout')}>
            {t('header.logout')}
          </NavDrawerStyledButton>
        </NavDrawerContent>
      </NavDrawerContainer>
      <LeaveConfirmation
        showQuotePopup={showQuotePopup}
        setQuoteShowPopup={setShowQuotePopup}
        showClaimPopup={showClaimPopup}
        setClaimShowPopup={setShowClaimPopup}
        nxtRoute={nxtRoute}
        setFileClaimPopup={setFileClaimPopup}
      />
      <FileClaimPopup showPopup={fileClaimPopup} setShowPopup={setFileClaimPopup} />

      {navDrawerOpen && <Backdrop onClick={toggleDrawer} />}
    </>
  );
};

export interface NavDrawerAccountProps {
  onViewProfileClick: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>, pathname: string) => void;
  onProfileLogoutClick: (pathname: string) => void;
}

export const NavDrawerAccount: FC<NavDrawerAccountProps> = ({ onViewProfileClick, onProfileLogoutClick }) => {
  const { data, isError, isLoading, error } = useCustomerInformation();

  const setUserDetail = useUserStore((state) => state.setUserDetail);

  if (isLoading) {
    return (
      <FlexBox flexType="container" justify="center" align="center">
        <Loader />
      </FlexBox>
    );
  }

  if (isError || !data || !data.content) {
    return <Error errorMessage={error?.message} />;
  }

  setUserDetail(data);
  const { name } = data.content;

  return (
    <NavAccountIconName
      name={name}
      onViewProfileClick={onViewProfileClick}
      onProfileLogoutClick={onProfileLogoutClick}
    />
  );
};

interface NavAccountIconNameProps {
  name: string;
  onViewProfileClick: (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>, pathname: string) => void;
  onProfileLogoutClick: (pathname: string) => void;
}

const NavAccountIconName: FC<NavAccountIconNameProps> = ({ name, onViewProfileClick, onProfileLogoutClick }) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <FlexBox flexType="container" align="center">
      <HeaderAccountIcon color={theme.palette.neutral.white} onClick={() => onProfileLogoutClick('/profile')}>
        {useAccountName(name)}
      </HeaderAccountIcon>
      <NavDrawerAccountDetails>
        <Typography color={theme.palette.neutral.white} fontType={FontType.COPY_LARGE}>
          {name}
        </Typography>
        <NavDrawerSeeProfileLink href="/profile" onClick={(e) => onViewProfileClick(e, '/profile')}>
          {t('header.seeProfile')}
        </NavDrawerSeeProfileLink>
      </NavDrawerAccountDetails>
    </FlexBox>
  );
};

export default NavDrawer;
